<template>
  <v-dialog
      v-model="dialog"
      :width="dialogWidth"
      @keydown.enter.prevent="onenter"
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
        :style="{ height: dialogHeight + 'px' }"
    >
      <v-card-title>
        <span class="headline">Talk ({{ this.rules.name }})</span>
        <v-spacer/>
        <r-authorize
            :master="masterAccess"
            :locked.sync="model.locked"
            :enabled.sync="model.enabled"
        />
      </v-card-title>
      <v-card-text>
        <v-col cols="4" sm="2" md="2">
          <v-btn @click="showPreview()" class="mr-2" small>View JSON</v-btn>
        </v-col>
        <v-row dense>
          <v-col cols="12" md="12" sm="12" class="mb-n5">
            <v-text-field
                label="ID:"
                v-model.trim="model.id"
                v-ascii-only
                outlined
                dense
            />
          </v-col>
          <v-col cols="12" md="6" sm="6">
            <v-text-field
                label="Name:"
                v-model.trim="model.name"
                v-ascii-only
            />
          </v-col>
          <v-col cols="12" md="6" sm="6">
            <v-text-field
                label="Key:"
                v-model.trim="model.key"
                v-ascii-only
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="4" sm="4">
            <r-hero-kind :model.sync="model.owner" label="Owner:"/>
          </v-col>
          <v-col cols="12" md="4" sm="4">
            <v-text-field label="Delay on show:" v-model.number="model.delayOnShow" hide-details/>
          </v-col>
          <v-col cols="12" md="4" sm="4">
            <v-checkbox v-model="model.resetOnClose" label="Reset On Close" hide-details/>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="12" sm="12"><br></v-col>
          <v-expansion-panels>
            <r-guard-panel :model.sync="model.guard" title="Guard Config"/>
            <r-trigger-panel :items.sync="model.triggersAvailable" title="Triggers - Available"/>
          </v-expansion-panels>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn @click="cancel" color="blue darken-1" text>
          Cancel
        </v-btn>
        <v-btn @click="save" color="blue darken-1" text v-if="!lockedAssess || masterAccess">
          Save
        </v-btn>
      </v-card-actions>
      <div class="resize-handle" @mousedown="startResize"></div>
      <r-preview-dialog ref="preview-dialog"></r-preview-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import VueTypes from "vue-types";
import {ViewRules} from "@/common/ViewRules";
import {TalkModel} from "@/modules/redactor/models/talks/TalkModel";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";

import RTriggerPanel from "@/modules/redactor/views/trigger/r-trigger-panel";
import RGuardPanel from "@/modules/redactor/views/guard/r-guard-panel";
import RHeroKind from "@/modules/redactor/views/hero/r-hero-kind";
import RAuthorize from "@/modules/redactor/views/common/r-authorize";
import RPreviewDialog from "@/modules/redactor/views/preview/r-preview-dialog.vue";

export default {
  name: "r-talk-dialog",
  components: {RPreviewDialog, RAuthorize, RHeroKind, RGuardPanel, RTriggerPanel},
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    model: VueTypes.objectOf(TalkModel),
    rules: ViewRules.READONLY,
    dialogWidth: 575,
    dialogHeight: 468,
    resizing: false,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 575,
    resizeStartHeight: 468,
    viewportPadding: 32
  }),
  computed: {
    lockedAssess() {
      return this.model.locked;
    },
    masterAccess() {
      return RedactorUtils.hasMasterAccess();
    }
  },
  methods: {
    show(rules, model) {
      this.dialog = true;
      this.rules = rules;
      this.model = model || new TalkModel();

      if (ViewRules.COPY.equal(this.rules)) {
        this.model.locked = false;
      }

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    save() {
      if (this.model.valid()) {
        this.resolve(this.model);
        this.discard();
      } else {
        this.$alert("Invalid config!");
      }
    },
    cancel() {
      this.reject();
      this.discard();
    },
    discard() {
      this.dialog = false;
      this.stopResize();
    },
    onenter() {
      if (!this.lockedAssess || this.masterAccess) {
        this.save();
      } else {
        this.cancel();
      }
    },
    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;
      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },
    onResize(e) {
      if (!this.resizing) return;

      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;

      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;

      const minWidth = 400;
      const minHeight = 260;

      if (newWidth < minWidth) newWidth = minWidth;
      if (newHeight < minHeight) newHeight = minHeight;

      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },
    stopResize() {
      if (!this.resizing) return;
      this.resizing = false;
      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    },
    showPreview() {
      this.$refs['preview-dialog'].show(this.rules, this.model, TalkModel)
        .then((result) => {
          Object.assign(this.model, result);
        })
        .catch((e) => {
          this.$alert("Failed to parse config!" + e.message);
        });
    }
  },
  beforeDestroy() {
    this.stopResize();
  }
};
</script>

<style scoped>
.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}
.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}
.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>